import React, { useState, useEffect } from "react";

import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import { fetchReferrals, updateReferralStatus, fetchReferralStatusOptions } from "../api/api";

function errorMessage(err) {
  return err.response?.data?.detail || "Something went wrong. Please try again.";
}

function AdminReferrals() {

  const [referrals, setReferrals] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");

  // referralId -> pending (not-yet-confirmed) value, same pattern as the
  // zone/status editors on /plots — nothing saves until confirmed.
  const [pendingStatus, setPendingStatus] = useState({});
  const [pendingReward, setPendingReward] = useState({});
  const [rowUpdating, setRowUpdating] = useState(null);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [referralData, statusData] = await Promise.all([
        fetchReferrals(),
        fetchReferralStatusOptions().catch(() => []),
      ]);
      setReferrals(referralData);
      setStatusOptions(statusData);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // App referrals aren't admin-curated (their status moves through a fixed
  // install/register/approve/first-login lifecycle automatically — see
  // _app_referral_status() in referrals.py's backend), so they're kept out
  // of this project-pipeline table and shown read-only below instead.
  const projectReferrals = referrals.filter((r) => r.referral_type !== "app");
  const appReferrals = referrals.filter((r) => r.referral_type === "app");

  const filteredReferrals = projectReferrals.filter((r) => {
    if (statusFilter && String(r.status_id) !== statusFilter) return false;
    if (search) {
      const q = search.trim().toLowerCase();
      const haystack = `${r.referred_name} ${r.referrer_name || ""} ${r.referred_email || ""} ${r.referred_mobile || ""}`.toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    return true;
  });

  const countByStatus = (statusId) => projectReferrals.filter((r) => r.status_id === statusId).length;

  const confirmStatusChange = async (referralId) => {
    const statusId = pendingStatus[referralId];
    setRowUpdating(referralId);
    setError("");
    try {
      const updated = await updateReferralStatus(referralId, { status_id: statusId });
      setReferrals((prev) => prev.map((r) => (r.id === referralId ? updated : r)));
      setPendingStatus((prev) => {
        const next = { ...prev };
        delete next[referralId];
        return next;
      });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setRowUpdating(null);
    }
  };

  const cancelStatusChange = (referralId) => {
    setPendingStatus((prev) => {
      const next = { ...prev };
      delete next[referralId];
      return next;
    });
  };

  const confirmRewardChange = async (referralId) => {
    const rewardGiven = pendingReward[referralId];
    setRowUpdating(referralId);
    setError("");
    try {
      const updated = await updateReferralStatus(referralId, { reward_given: rewardGiven });
      setReferrals((prev) => prev.map((r) => (r.id === referralId ? updated : r)));
      setPendingReward((prev) => {
        const next = { ...prev };
        delete next[referralId];
        return next;
      });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setRowUpdating(null);
    }
  };

  const cancelRewardChange = (referralId) => {
    setPendingReward((prev) => {
      const next = { ...prev };
      delete next[referralId];
      return next;
    });
  };

  return (

    <div>

      <Navbar />

      <Sidebar />

      <div className="admin-content">

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
          <h1 style={{ marginTop: 0, marginBottom: 0 }}>Referrals</h1>
          <div className="service-count">{projectReferrals.length}</div>
        </div>

        <p style={{ color: "#666" }}>
          Leads submitted by customers through Refer a Friend — who referred whom, and the follow-up status.
        </p>

        {error && <p className="login-error" style={{ color: "red" }}>{error}</p>}

        <div className="filter-row">

          <button className={statusFilter === "" ? "active-filter" : ""} onClick={() => setStatusFilter("")}>
            All ({projectReferrals.length})
          </button>

          {statusOptions.map((s) => (
            <button
              key={s.id}
              className={statusFilter === String(s.id) ? "active-filter" : ""}
              onClick={() => setStatusFilter(String(s.id))}
            >
              {s.name} ({countByStatus(s.id)})
            </button>
          ))}

        </div>

        <input
          type="text"
          placeholder="Search by referrer, friend, email, or mobile"
          className="search-bar"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ marginBottom: "20px", width: "100%" }}
        />

        {loading && <p>Loading referrals…</p>}

        {!loading && filteredReferrals.length === 0 && <p>No referrals match these filters.</p>}

        {!loading && filteredReferrals.length > 0 && (
          <div className="table-card">
            <table className="service-table">
              <thead>
                <tr>
                  <th>Referred By</th>
                  <th>Friend</th>
                  <th>Contact</th>
                  <th>Project</th>
                  <th>Submitted</th>
                  <th>Status</th>
                  <th>Reward</th>
                </tr>
              </thead>
              <tbody>
                {filteredReferrals.map((r) => (
                  <tr key={r.id}>
                    <td>{r.referrer_name || "—"}</td>
                    <td>{r.referred_name}</td>
                    <td>{[r.referred_mobile, r.referred_email].filter(Boolean).join(" • ") || "—"}</td>
                    <td>{r.project || "—"}</td>
                    <td>{new Date(r.created_at).toLocaleDateString()}</td>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <select
                          value={r.id in pendingStatus ? pendingStatus[r.id] : r.status_id}
                          onChange={(e) =>
                            setPendingStatus((prev) => ({ ...prev, [r.id]: Number(e.target.value) }))
                          }
                          disabled={rowUpdating === r.id}
                        >
                          {statusOptions.map((s) => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                          ))}
                        </select>

                        {r.id in pendingStatus && pendingStatus[r.id] !== r.status_id && (
                          <>
                            <button
                              type="button"
                              title="Confirm"
                              onClick={() => confirmStatusChange(r.id)}
                              disabled={rowUpdating === r.id}
                              style={{ background: "#2e7d32", color: "white", border: "none", borderRadius: "4px", width: "26px", height: "26px", cursor: "pointer" }}
                            >
                              ✓
                            </button>
                            <button
                              type="button"
                              title="Cancel"
                              onClick={() => cancelStatusChange(r.id)}
                              disabled={rowUpdating === r.id}
                              style={{ background: "#dc2626", color: "white", border: "none", borderRadius: "4px", width: "26px", height: "26px", cursor: "pointer" }}
                            >
                              ✕
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer" }}>
                          <input
                            type="checkbox"
                            checked={r.id in pendingReward ? pendingReward[r.id] : r.reward_given}
                            onChange={(e) =>
                              setPendingReward((prev) => ({ ...prev, [r.id]: e.target.checked }))
                            }
                            disabled={rowUpdating === r.id}
                          />
                          {(r.id in pendingReward ? pendingReward[r.id] : r.reward_given) ? "Given" : "Pending"}
                        </label>

                        {r.id in pendingReward && pendingReward[r.id] !== r.reward_given && (
                          <>
                            <button
                              type="button"
                              title="Confirm"
                              onClick={() => confirmRewardChange(r.id)}
                              disabled={rowUpdating === r.id}
                              style={{ background: "#2e7d32", color: "white", border: "none", borderRadius: "4px", width: "26px", height: "26px", cursor: "pointer" }}
                            >
                              ✓
                            </button>
                            <button
                              type="button"
                              title="Cancel"
                              onClick={() => cancelRewardChange(r.id)}
                              disabled={rowUpdating === r.id}
                              style={{ background: "#dc2626", color: "white", border: "none", borderRadius: "4px", width: "26px", height: "26px", cursor: "pointer" }}
                            >
                              ✕
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "30px 0 10px" }}>
          <h1 style={{ marginTop: 0, marginBottom: 0, fontSize: "20px" }}>App Referrals</h1>
          <div className="service-count">{appReferrals.length}</div>
        </div>
        <p style={{ color: "#666" }}>
          Tracked automatically: Installed once someone taps a shared app link, Pending Approval once they
          register, Successful once their registration is approved and they've logged in for the first time.
        </p>

        {!loading && appReferrals.length === 0 && <p>No app referrals yet.</p>}

        {!loading && appReferrals.length > 0 && (
          <div className="table-card">
            <table className="service-table">
              <thead>
                <tr>
                  <th>Referred By</th>
                  <th>Friend</th>
                  <th>Shared</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {appReferrals.map((r) => (
                  <tr key={r.id}>
                    <td>{r.referrer_name || "—"}</td>
                    <td>{r.referred_name || "—"}</td>
                    <td>{new Date(r.created_at).toLocaleDateString()}</td>
                    <td>{r.status_name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      </div>

    </div>

  );

}

export default AdminReferrals;
